import { zodResolver } from '@hookform/resolvers/zod';
import {
  Button,
  Card,
  CheckboxGroupField,
  Field,
  SkeletonLoader,
  useToastContext,
} from '@payaca/ambrosia';

import { FC, useEffect } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import { z } from 'zod/v4';
import useUpdateFieldServiceSupplierIds from '../../../api/mutations/account/useUpdateFieldServiceSupplierIds';
import useGetMyAccountFieldServiceSettings from '../../../api/queries/me/useGetMyAccountFieldServiceSettings';
import { useGetAllPaginatedSuppliers } from '../../../api/queries/suppliers/useGetSuppliers';

const fieldServiceSettingsSchema = z.object({
  supplierIds: z.array(z.string()),
});
type FieldServiceSettingsSchema = z.infer<typeof fieldServiceSettingsSchema>;

type Props = {
  readOnly: boolean;
};

const CompanySettingsFieldServiceSuppliers: FC<Props> = (props) => {
  const { readOnly } = props;
  const { pushSuccess, pushError } = useToastContext();

  const { data: fieldServiceSettings, refetch } =
    useGetMyAccountFieldServiceSettings();
  const { suppliers } = useGetAllPaginatedSuppliers({}, 100);
  const { mutateAsync: updateFieldServiceSupplierIdsMutation } =
    useUpdateFieldServiceSupplierIds();

  const formMethods = useForm<FieldServiceSettingsSchema>({
    resolver: zodResolver(fieldServiceSettingsSchema),
    defaultValues: {
      supplierIds: fieldServiceSettings?.allowedSupplierIds || [],
    },
  });
  useEffect(() => {
    if (fieldServiceSettings) {
      formMethods.reset({
        supplierIds: fieldServiceSettings?.allowedSupplierIds || [],
      });
    }
    // oxlint-disable-next-line eslint-plugin-react-hooks(exhaustive-deps) -- FIXME: pre-existing error: React Hook useEffect has a missing dependency: 'formMethods'
  }, [fieldServiceSettings, formMethods.reset]);

  const onSubmit = async (data: FieldServiceSettingsSchema) => {
    const response = await updateFieldServiceSupplierIdsMutation({
      allowedSupplierIds: data.supplierIds,
    });
    if (
      response.updateFieldServiceSupplierIds?.__typename ===
      'UpdateFieldServiceSupplierIdsResponseSuccess'
    ) {
      pushSuccess('Saved successfully');
      formMethods.reset({
        supplierIds: response.updateFieldServiceSupplierIds.allowedSupplierIds,
      });
      void refetch();
    } else {
      pushError(`We couldn't save your changes`);
    }
  };

  return (
    <Card sizeVariant="lg">
      <Card.Body className="space-y-4">
        <h2>Suppliers</h2>
        <p>
          Choose which Suppliers you can choose Materials from on the Field
          Service app.
        </p>
        {!fieldServiceSettings?.allowedSupplierIds || !suppliers ? (
          <SkeletonLoader />
        ) : (
          <FormProvider {...formMethods}>
            <form onSubmit={formMethods.handleSubmit(onSubmit)}>
              <Controller
                name="supplierIds"
                render={({ field: { onChange, value } }) => {
                  return (
                    <Field.Legacy name="supplierIds">
                      <CheckboxGroupField
                        horizontal
                        cols={3}
                        onChange={(v) => onChange(v)}
                        options={suppliers?.map((s) => ({
                          label: s.name,
                          value: s.id,
                        }))}
                        value={value}
                      />
                    </Field.Legacy>
                  );
                }}
              />
              <div className="flex justify-end mt-4">
                <Button
                  type="submit"
                  disabled={readOnly || !formMethods.formState.isDirty}
                >
                  Save
                </Button>
              </div>
            </form>
          </FormProvider>
        )}
      </Card.Body>
      <Prompt
        when={formMethods.formState.isDirty}
        message="There are unsaved changes on the page, are you sure you want to leave?"
      />
    </Card>
  );
};

export default CompanySettingsFieldServiceSuppliers;
